/** The shape of `assets.json` and `objects.json`, plus the pure geometry that
 *  follows from a frame's anchor. No GPU, no DOM - `test/world.spec.ts` covers it.
 */

/**
 * One packed frame.
 *
 * `anchorX`/`anchorY` are the important pair: for a map object they are the
 * GROUND LINE - the bottom-centre of the opaque bounding box - not the corner
 * of the bounding box. Placing a sprite by its bbox corner is what makes a 2D
 * strategy map read as flat.
 */
export interface FrameMeta {
  readonly sheet: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly anchorX: number;
  readonly anchorY: number;
  readonly trimX: number;
  readonly trimY: number;
  readonly srcW: number;
  readonly srcH: number;
}

export interface SheetMeta {
  readonly id: string;
  /** The pack group (`terrain`, `units`, ...); the id's prefix on old manifests. */
  readonly group?: string;
  readonly file: string;
  readonly w: number;
  readonly h: number;
}

export interface AtlasManifest {
  readonly sheets: readonly SheetMeta[];
  readonly keys: Readonly<Record<string, FrameMeta>>;
}

/** `objects.json` -> `meta.objects[id]`. Carries the tile-space footprint. */
export interface ObjectMeta {
  readonly category: string;
  readonly footprint: readonly [number, number];
  readonly spritePx: readonly [number, number];
  readonly anchorTile: readonly [number, number];
  readonly drawOffsetPx: readonly [number, number];
  readonly passability: readonly string[];
  readonly entranceTile: readonly [number, number];
  readonly variants: readonly string[];
}

export interface PixelRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Where a frame lands in world pixels when its anchor is pinned to `(gx, gy)`.
 *
 * The anchor is expressed in the *packed* frame's own coordinates, not the
 * untrimmed source's, under one of the packer's three policies
 * (`tools/assets/verify.ts`):
 *
 *   ground    `anchorX = (w+1)>>1`, `anchorY = h`  - map objects
 *   centre    `anchorX = (w+1)>>1`, `anchorY = (h+1)>>1`
 *   top-left  `anchorX = -trimX`,   `anchorY = -trimY`  - town buildings, which
 *             composite onto a backdrop at authored positions and therefore
 *             need the untrimmed origin back
 *
 * Subtracting the anchor is correct for all three, and adding the trim on top
 * would double-count it for the third. Because trimming removes the fully
 * transparent margin, a ground-anchored frame's `w` x `h` *is* the opaque box -
 * which is what the contact shadow below is derived from.
 */
export function frameBounds(gx: number, gy: number, f: FrameMeta): PixelRect {
  return {
    x: gx - f.anchorX,
    y: gy - f.anchorY,
    w: f.w,
    h: f.h,
  };
}

/**
 * The opaque footprint a contact shadow is derived from: the trimmed box, which
 * is the tight box around non-transparent pixels, projected onto the ground.
 * Width comes from the trimmed box; depth is a fraction of it, because a shadow
 * cast by a near-overhead light is an ellipse much shallower than it is wide.
 */
export function footprintEllipse(
  gx: number, gy: number, f: FrameMeta,
): { readonly cx: number; readonly cy: number; readonly rx: number; readonly ry: number } {
  const b = frameBounds(gx, gy, f);
  const rx = Math.max(4, b.w * 0.42);
  return {
    cx: b.x + b.w / 2,
    // Sit the ellipse a hair above the ground line so the sprite's own base
    // pixels overlap it; a shadow whose top edge is visible reads as a decal.
    cy: gy - Math.max(2, rx * 0.10),
    rx,
    ry: Math.max(3, rx * 0.34),
  };
}

export const rectsIntersect = (a: PixelRect, b: PixelRect): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
